import type React from 'react';
import { memo } from 'react';
import { ErrorBoundary } from '../../../../components/ErrorBoundary';
import { dummyComponentNode } from '../../../../consts/component';
import { useRegistryContext } from '../../../context/RegistryProvider';
import type { BlockRendererWrapperProps } from '../../../context/RegistryProvider/types';
import { useBlockFromPage } from '../../../stores/InterfaceStore';
import { isEmptyBlock } from '../../../utils/block';
import { useSlot } from '../../page/InterfacePageProviders/context/SlotsProvider';
import type { WithBlockStateProps } from '../types';

export const withBlockPageAndRuntime = (
  WrappedComponent: React.ComponentType<WithBlockStateProps>,
) => {
  function WithBlockPageAndRuntimeWrapper(props: BlockRendererWrapperProps) {
    const { blockId } = props;
    const block = useBlockFromPage({ blockId });
    const { slot } = useSlot(blockId ?? '');
    const { registry } = useRegistryContext();
    const shouldSkipErrorBoundary = registry.getShouldBlockSkipErrorBoundary(
      block?.component.componentType,
    );

    if (slot) {
      return slot;
    }

    if (isEmptyBlock(blockId)) {
      return null;
    }

    if (!block) {
      return null;
    }

    if (shouldSkipErrorBoundary) {
      return <WrappedComponent {...props} block={block} />;
    }

    return (
      <ErrorBoundary fallback={dummyComponentNode}>
        <WrappedComponent {...props} block={block} />
      </ErrorBoundary>
    );
  }

  return memo(WithBlockPageAndRuntimeWrapper);
};
